import { GridFilterItem } from '@mui/x-data-grid-pro';

import { QueryOption } from '../@types';
import { httpClient, httpGet, HttpMethod } from './http-client';
import {
  MerchantDamagePartAdditionalData,
  MerchantDamageTemplateQuery,
  MerchantDamagePartSerialQueryData,
  PartListResponse,
  NSPartLocationList,
  SerailizedPartsList,
} from '../@types/part.type';
import { mapNumericEqualsOperatorForWire } from '../utils/filterModel';

// Response type for part additional data update
interface PartAdditionalDataResponse {
  id: number;
  success: boolean;
}

/** Override the default 15s axios timeout for multi-paste serial1 isAnyOf
 *  lookups. The BFF's query plan for `serial1 IN (long list)` is much slower
 *  than a single-serial substring search and consistently exceeds 15s on
 *  ~50+ paste lists, so the request was aborting client-side before the BFF
 *  could finish. Old CMS2 had no timeout at all on these calls. 60s matches
 *  the user expectation for a bulk lookup; BFF query optimisation is on the
 *  Backlog so this can come back down. */
const MULTI_PASTE_PARTS_TIMEOUT_MS = 60_000;

/**
 * Parts-list filter fields that map to *numeric* (integer) columns on the
 * `PartList` model in api-inventory-mgmt (`assetId`, `partTypeId`,
 * `quantityInStock`, `nodeId`). `exceptionServiceId` is intentionally absent —
 * it is a string column, so it must keep its case-insensitive `ILIKE` matching.
 */
const NUMERIC_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'assetId',
  'partTypeId',
  'quantityInStock',
  'nodeId',
]);

/**
 * Translate a filter operator to the form the parts BFF understands for a given
 * field before it goes on the wire.
 *
 * Why: api-inventory-mgmt maps `equals` (via lib-framework `getSequelizeOperator`)
 * to `Op.iLike`. On the integer columns above `integer ILIKE text` is invalid,
 * so the query errors and the filter returns nothing — this is why the Parts
 * "Part Type ID" / "Node ID" / "Asset ID" custom filters never matched. `=`
 * (→ `Op.eq`) is the only numeric-safe operator on the inventory BFF (unlike
 * api-services, even `isAnyOf` is `ILIKE`-based here), so we rewrite
 * `equals` → `=` for these fields only. Everything else passes through.
 *
 * `contains` on these numeric columns has no numeric-safe equivalent yet — it
 * needs the BFF to cast the column to text — so it is left untouched (and still
 * non-functional) pending that backend change. Tracked in the backlog.
 */
export const mapPartFilterOperatorForWire = (
  field: string | undefined,
  operator: string | undefined
): string | undefined => mapNumericEqualsOperatorForWire(field, operator, NUMERIC_FILTER_FIELDS);

/**
 * Parts-list fields the grid needs on every row regardless of whether the user
 * shows the column.
 *
 * `excludedColumns` is not a display hint: bff-web forwards it to
 * api-inventory-mgmt, which turns it into a Sequelize `attributes: { exclude }`,
 * so an unticked column is genuinely **absent** from each row object. Two cells
 * read `serialised` rather than their own field — Qty renders 1 on a serialised
 * row (the `partlist` view selects NULL for those, one serial being one unit)
 * and the Create Repair Job action stays disabled on consumable rows — and
 * `serialised` ships unticked in the default column selection. It is a CASE over
 * an already-joined column, so keeping it costs nothing in the query.
 */
const ALWAYS_INCLUDED_PART_FIELDS: ReadonlySet<string> = new Set(['serialised']);

/** Drop the always-needed fields from a comma-separated excludedColumns list. */
export const keepAlwaysIncludedPartFields = (excludedColumns?: string): string | undefined => {
  if (!excludedColumns) return excludedColumns;
  return excludedColumns
    .split(',')
    .filter((field) => field && !ALWAYS_INCLUDED_PART_FIELDS.has(field.trim()))
    .join(',');
};

export const getAllParts = (
  queryOptions: QueryOption,
  serialSearch?: string,
  excludedColumns?: string,
  obfuscation?: string,
  signal?: AbortSignal
): Promise<PartListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  let url = `/parts?page=${page + 1}&limit=${pageSize}&serial_search=${serialSearch}&excludedColumns=${keepAlwaysIncludedPartFields(excludedColumns)}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = mapPartFilterOperatorForWire(field, filter?.operator);
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  if (obfuscation) {
    url += `&obfuscation=${obfuscation}`;
  }

  const hasMultiPasteSerialFilter = filterModel.items?.some(
    (i) =>
      i.field === 'serial1' &&
      i.operator === 'isAnyOf' &&
      Array.isArray(i.value) &&
      i.value.length > 1
  );
  if (hasMultiPasteSerialFilter) {
    return httpClient<PartListResponse>(
      url,
      HttpMethod.Get,
      undefined,
      undefined,
      MULTI_PASTE_PARTS_TIMEOUT_MS,
      signal
    );
  }

  return httpGet<PartListResponse>(url, signal);
};

export const getNSPartByPartTypeAndLocation = (
  partTypeId: number,
  locationId: number,
  isReturn: boolean = false,
  isIncludeLocationChildren: boolean = false,
  signal?: AbortSignal
): Promise<NSPartLocationList[]> => {
  return httpGet<NSPartLocationList[]>(
    `/nsparts/search?partTypeId=${partTypeId}&locationId=${locationId}&isReturn=${isReturn}&isIncludeLocationChildren=${isIncludeLocationChildren}`,
    signal
  );
};

export const getPartsLogAgainstService = (
  serviceId: number,
  locationTypeCode: string,
  signal?: AbortSignal
): Promise<SerailizedPartsList[]> => {
  return httpGet<SerailizedPartsList[]>(
    `/partsLogAgainstService/${serviceId}?locationTypeCode=${encodeURIComponent(locationTypeCode)}`,
    signal
  );
};

export const getMerchantDamagePartSerialData = (
  queryOptions: MerchantDamageTemplateQuery,
  signal?: AbortSignal
): Promise<MerchantDamagePartSerialQueryData[]> => {
  const { serviceId, mainLocationNodeId } = queryOptions;
  const url = `/partHistory/${serviceId}/location/${mainLocationNodeId}/outPartActions`;
  return httpGet<MerchantDamagePartSerialQueryData[]>(url, signal);
};

export const updatePartAdditionalData = (
  queryOptions: MerchantDamagePartAdditionalData,
  partId: number
): Promise<PartAdditionalDataResponse> => {
  const url = `/parts/${partId}/additionalData/merchantDamageData`;
  return httpClient<PartAdditionalDataResponse>(url, HttpMethod.Put, queryOptions);
};
